import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * useFetchOnce - A reusable hook to prevent duplicate API calls
 * 
 * This hook ensures that an API action is called only once per unique dependency value,
 * preventing unnecessary network requests during re-renders.
 * 
 * @param {Object} config - Configuration object
 * @param {Function} config.action - Redux action creator function
 * @param {Object} config.actionParams - Parameters to pass to the action (e.g., { Query: `/${id}` })
 * @param {any} config.dependency - Unique value to track (e.g., userId, patientPlanId). When this changes, a new fetch is triggered.
 * @param {Function} [config.onSuccess] - Optional callback function called on successful response
 * @param {Function} [config.onError] - Optional callback function called on error
 * @param {boolean} [config.enabled=true] - Whether the fetch should be enabled
 * @param {Function} [config.selector] - Optional Redux selector to check for existing data
 * @param {Function} [config.shouldFetch] - Optional function to determine if fetch is needed based on selected data
 * 
 * @returns {Object} - { loading, error, hasFetched, refetch }
 * 
 * @example
 * // Basic usage
 * const { loading, error, hasFetched } = useFetchOnce({
 *   action: ActionGetCCPaperDetail,
 *   actionParams: { Query: `/${userId}` },
 *   dependency: userId,
 *   onSuccess: (response) => console.log('Fetched:', response),
 * });
 * 
 * @example
 * // With Redux selector to check existing data
 * const { loading, error } = useFetchOnce({
 *   action: ActionGetPatientPlanList,
 *   actionParams: { Query: `/${patientPlanId}` },
 *   dependency: patientPlanId,
 *   selector: (state) => state?.appConfig?.patientPlanList,
 *   shouldFetch: (data) => !data || data._id !== patientPlanId,
 * });
 */
export const useFetchOnce = ({
    action,
    actionParams,
    dependency,
    onSuccess,
    onError,
    enabled = true,
    selector,
    shouldFetch,
}) => {
    const dispatch = useDispatch();

    // Track which dependency value we've already fetched
    const fetchedDependencyRef = useRef(null);
    const isFetchingRef = useRef(false);

    // State for loading and error
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);

    // Get selected data from Redux if selector is provided
    const selectedData = useSelector(selector || (() => null));

    // Main fetch effect
    useEffect(() => {
        // Skip if not enabled or no dependency
        if (!enabled || dependency === null || dependency === undefined) {
            return;
        }

        // Check if we've already fetched for this dependency
        const alreadyFetchedThisDependency = fetchedDependencyRef.current === dependency;

        // Check if we already have correct data in Redux
        const hasCorrectData = selector && shouldFetch
            ? !shouldFetch(selectedData)
            : false;

        // Skip if already fetched or already fetching or already have data
        if (alreadyFetchedThisDependency || isFetchingRef.current || hasCorrectData) {
            if (hasCorrectData) {
                console.log(`useFetchOnce: Using cached data for dependency: ${dependency}`);
            }
            return;
        }

        // Mark as fetching
        isFetchingRef.current = true;
        fetchedDependencyRef.current = dependency;
        setLoading(true);
        setError(null);


        // Dispatch the action
        dispatch(action(actionParams, (response) => {
            isFetchingRef.current = false;
            setLoading(false);
            setHasFetched(true);
            if (response?.statusCode === 200 || response?.success) {
                if (onSuccess) {
                    onSuccess(response);

                }
            } else {
                const errorMessage = response?.message || 'An error occurred';
                setError(errorMessage);
                if (onError) {
                    onError(response);
                }
            }
        }));

        // Cleanup function to reset fetching flag if component unmounts during fetch
        return () => {
            // Don't reset fetchedDependencyRef here to maintain cache
            const timer = setTimeout(() => {
                isFetchingRef.current = false;
            }, 500);
            return () => clearTimeout(timer);
        };
    }, [dependency, enabled, dispatch, action, actionParams, onSuccess, onError, selector, shouldFetch, selectedData]);

    // Function to force refetch (useful for manual refresh)
    const refetch = useCallback(() => {
        if (!dependency) return;

        // Reset the fetched ref to allow refetch
        fetchedDependencyRef.current = null;
        isFetchingRef.current = false;
        setHasFetched(false);

        // Trigger a new fetch by updating state
        setLoading(true);
        setError(null);
        dispatch(action(actionParams, (response) => {
            fetchedDependencyRef.current = dependency;
            isFetchingRef.current = false;
            setLoading(false);
            setHasFetched(true);
            if (response?.statusCode === 200 || response?.success) {
                if (onSuccess) {
                    onSuccess(response);
                }
            } else {
                const errorMessage = response?.message || 'An error occurred';
                setError(errorMessage);
                if (onError) {
                    onError(response);
                }
            }
        }));
    }, [dependency, dispatch, action, actionParams, onSuccess, onError]);

    return {
        loading,
        error,
        hasFetched,
        refetch,
    };
};

/**
 * useMultiFetchOnce - Hook for fetching multiple APIs with duplicate prevention
 * 
 * @param {Array<Object>} fetchConfigs - Array of fetch configurations
 * @returns {Object} - { loading, errors, hasFetched, refetchAll }
 * 
 * @example
 * const { loading, errors } = useMultiFetchOnce([
 *   {
 *     key: 'patientPlan',
 *     action: ActionGetPatientPlanList,
 *     actionParams: { Query: `/${patientPlanId}` },
 *     dependency: patientPlanId,
 *   },
 *   {
 *     key: 'treatmentSlot',
 *     action: ActionGetTreatmentSlotById,
 *     actionParams: { Query: `/${treatmentSlotId}` },
 *     dependency: treatmentSlotId,
 *   },
 * ]);
 */
export const useMultiFetchOnce = (fetchConfigs) => {
    const dispatch = useDispatch();

    // Track fetched dependencies for each config
    const fetchedDepsRef = useRef({});
    const isFetchingRef = useRef({});

    const [loadingStates, setLoadingStates] = useState({});
    const [errors, setErrors] = useState({});
    const [hasFetched, setHasFetched] = useState({});

    useEffect(() => {
        fetchConfigs.forEach((config) => {
            const { key, action, actionParams, dependency, onSuccess, onError, enabled = true } = config;

            // Skip if not enabled or no dependency
            if (!enabled || dependency === null || dependency === undefined) {
                return;
            }

            // Check if already fetched for this dependency
            if (fetchedDepsRef.current[key] === dependency || isFetchingRef.current[key]) {
                return;
            }

            // Mark as fetching
            isFetchingRef.current[key] = true;
            fetchedDepsRef.current[key] = dependency;

            setLoadingStates(prev => ({ ...prev, [key]: true }));
            setErrors(prev => ({ ...prev, [key]: null }));


            dispatch(action(actionParams, (response) => {
                isFetchingRef.current[key] = false;
                setLoadingStates(prev => ({ ...prev, [key]: false }));
                setHasFetched(prev => ({ ...prev, [key]: true }));

                if (response?.statusCode === 200 || response?.success) {
                    if (onSuccess) {
                        onSuccess(response);
                    }
                } else {
                    const errorMessage = response?.message || 'An error occurred';
                    setErrors(prev => ({ ...prev, [key]: errorMessage }));
                    if (onError) {
                        onError(response);
                    }
                }
            }));
        });
    }, [fetchConfigs, dispatch]);

    // Check if any fetch is loading
    const loading = Object.values(loadingStates).some(Boolean);

    // Refetch all APIs
    const refetchAll = useCallback(() => {
        fetchedDepsRef.current = {};
        isFetchingRef.current = {};
        setLoadingStates({});
        setErrors({});
        setHasFetched({});
        // The useEffect will trigger fetches again
    }, []);

    return {
        loading,
        loadingStates,
        errors,
        hasFetched,
        refetchAll,
    };
};

export default useFetchOnce;
